import { useCallback } from "react";

import { useObx } from "../../../hooks/useObx";
import ApiRequestPaths from "../../../constants/ApiRequestPaths";
import { IPatient } from "../../../types/Entities/Patient";
import { Lead } from "../../../types/Lead";
import { IPatientUpdate, IQualifyStepWrapperConfig } from "../Components/QualifyStepWrapper";

/** The payload sent to update patient on the backend. */
interface PatientUpdateRequest {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  email?: string;
  zipCode?: string;
  dateOfBirth?: string;
  centerId: string;
  distanceToCenter?: number;
  height?: { feet: number; inches: number };
  weight?: number;
  /** @remarks Intentionally using the legacy field name here. */
  opportunity?: Lead | null;
}

/** Hook for updating the persisted state of the patient. */
const useUpdatePatient: IQualifyStepWrapperConfig["UseUpdatePatient"] = ({ contactId, currentPatient }) => {
  const { res, err, loading, setReq } = useObx<IPatient, PatientUpdateRequest>("Put Patient", {
    method: "put"
  });

  const execute = useCallback(
    (patientUpdate: IPatientUpdate) => {
      // Send the patient with preserved read-only fields and opportunity from lead
      const payload: PatientUpdateRequest = {
        id: patientUpdate.id,
        firstName: patientUpdate.firstName,
        lastName: patientUpdate.lastName,
        phoneNumber: patientUpdate.phoneNumber,
        email: patientUpdate.email,
        zipCode: patientUpdate.zipCode,
        dateOfBirth: currentPatient?.dateOfBirth ?? patientUpdate.dateOfBirth,
        centerId: currentPatient?.centerId ?? patientUpdate.centerId,
        distanceToCenter: currentPatient?.distanceToCenter ?? patientUpdate.distanceToCenter,
        height: currentPatient?.height ?? patientUpdate.height,
        weight: currentPatient?.weight ?? patientUpdate.weight,
        opportunity: currentPatient?.lead
      };

      setReq(
        r =>
          r && {
            ...r,
            url: ApiRequestPaths.putPatientUrl(contactId, patientUpdate.id),
            payload
          }
      );
    },
    [contactId, currentPatient, setReq]
  );

  return { result: res?.payload || null, isLoading: loading, execute, error: Boolean(err) };
};

export default useUpdatePatient;
